import type { CartDiagnosticEvent } from "@ecommerce/store";
import { HttpLogger } from "../http";

const serializeError = (
    error: unknown,
    depth = 0,
    seen = new WeakSet<object>(),
): Record<string, unknown> => {
    if (depth >= 5) return { truncated: true, value: String(error) };

    if (typeof error !== "object" || error === null) {
        return { type: typeof error, value: String(error) };
    }

    if (seen.has(error)) return { circularReference: true };
    seen.add(error);

    if (error instanceof Error) {
        const errorWithCause = error as Error & {
            cause?: unknown;
            code?: unknown;
        };
        return {
            name: error.name,
            message: error.message,
            stack: error.stack ?? null,
            code: errorWithCause.code,
            cause:
                errorWithCause.cause === undefined
                    ? undefined
                    : serializeError(errorWithCause.cause, depth + 1, seen),
            enumerableProperties: Object.fromEntries(
                Object.entries(error).map(([key, value]) => [
                    key,
                    typeof value === "object" && value !== null
                        ? serializeError(value, depth + 1, seen)
                        : value,
                ]),
            ),
        };
    }

    return {
        type: error.constructor?.name ?? "Object",
        properties: Object.fromEntries(
            Object.entries(error).map(([key, value]) => [
                key,
                typeof value === "object" && value !== null
                    ? serializeError(value, depth + 1, seen)
                    : value,
            ]),
        ),
    };
};

const getStorageEnvironment = async (): Promise<Record<string, unknown>> => {
    let storageEstimate: StorageEstimate | null = null;
    let storagePersisted: boolean | null = null;
    let storageApiError: Record<string, unknown> | null = null;

    try {
        storageEstimate = (await navigator.storage?.estimate?.()) ?? null;
        storagePersisted = (await navigator.storage?.persisted?.()) ?? null;
    } catch (error) {
        storageApiError = serializeError(error);
    }

    return {
        storageEstimate,
        storagePersisted,
        storageApiError,
        localStorageEntryCount: (() => {
            try {
                return window.localStorage.length;
            } catch {
                return null;
            }
        })(),
        webLocksAvailable: typeof navigator.locks?.request === "function",
        indexedDbAvailable: typeof indexedDB !== "undefined",
        secureContext: window.isSecureContext,
        cookieEnabled: navigator.cookieEnabled,
        online: navigator.onLine,
        language: navigator.language,
        visibilityState: document.visibilityState,
    };
};

export const reportCartDiagnosticToBackend = async (event: CartDiagnosticEvent): Promise<void> => {
    const serializedError = serializeError(event.error);
    const errorName =
        typeof serializedError.name === "string" ? serializedError.name : "CartDiagnostic";
    const errorMessage =
        typeof serializedError.message === "string"
            ? serializedError.message
            : "Se detectó un problema de persistencia del carrito.";
    const errorCode = typeof serializedError.code === "string" ? serializedError.code : "UNKNOWN";

    await HttpLogger.logReactError({
        app: "FrontTienda",
        environment: (window as any).APP_ENV ?? "prod",
        release: (window as any).APP_VERSION ?? undefined,
        url: window.location.href,
        path: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent,
        timestampUtc: event.timestampUtc,
        errorName,
        errorMessage: `[Cart][${event.kind}][${errorCode}] ${errorMessage}`,
        errorStack: typeof serializedError.stack === "string" ? serializedError.stack : undefined,
        componentStack: null,
        extra: {
            cartDiagnostic: {
                kind: event.kind,
                error: serializedError,
                context: event.context,
                storageEnvironment: await getStorageEnvironment(),
            },
        },
    });
};
